import { useEffect, useState } from "react";

// WhatsApp-style text-story fonts. Google Fonts are injected on demand (first use),
// so the app bundle/startup is unaffected. `probe` lets us await the actual glyphs.
export const STORY_FONTS = [
  { key: "classic", label: "Classique", style: { fontFamily: "'DM Sans', system-ui, sans-serif" } },
  { key: "elegant", label: "Élégante", style: { fontFamily: "'Playfair Display', Georgia, serif", fontStyle: "italic" }, google: "Playfair+Display:ital,wght@1,700", probe: "italic 700 16px 'Playfair Display'" },
  { key: "script", label: "Manuscrite", style: { fontFamily: "'Caveat', 'Segoe Script', cursive", fontWeight: 700 }, google: "Caveat:wght@700", probe: "700 16px Caveat", boost: 1.25 },
  { key: "impact", label: "Impact", style: { fontFamily: "'Anton', Impact, 'Arial Narrow', sans-serif", fontWeight: 400, letterSpacing: "0.02em" }, google: "Anton", probe: "400 16px Anton" },
];
export const DEFAULT_STORY_FONT = STORY_FONTS[0].key;

const fontOf = (key) => STORY_FONTS.find((f) => f.key === key) || STORY_FONTS[0];
const loading = new Map();

export const ensureStoryFont = (key) => {
  const f = fontOf(key);
  if (!f.google || typeof document === "undefined") return Promise.resolve();
  if (!loading.has(f.key)) {
    loading.set(f.key, new Promise((resolve) => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = `https://fonts.googleapis.com/css2?family=${f.google}&display=swap`;
      link.onload = () => (document.fonts?.load ? document.fonts.load(f.probe).catch(() => {}) : Promise.resolve()).then(resolve);
      link.onerror = () => resolve();
      document.head.appendChild(link);
    }));
  }
  return loading.get(f.key);
};

export const ensureAllStoryFonts = () => STORY_FONTS.forEach((f) => ensureStoryFont(f.key));

// Inline style for a story's font — also triggers the on-demand load (idempotent).
export const storyFontStyle = (key) => { ensureStoryFont(key); return fontOf(key).style; };

// Hand-written fonts have a small x-height → nudge the auto-fit size up.
export const storyFontScale = (key, scale) => (Number(scale) || 1) * (fontOf(key).boost || 1);

// Changes once the font's glyphs are available → pass as AutoFitText `refitKey`.
export const useStoryFontReady = (key) => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let on = true;
    ensureStoryFont(key).then(() => { if (on) setTick((n) => n + 1); });
    return () => { on = false; };
  }, [key]);
  return `${key || DEFAULT_STORY_FONT}:${tick}`;
};
